import React, { useState, useEffect, useMemo } from "react";
import {
  Typography,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  message,
  Popconfirm,
  Radio,
} from "antd";
import {
  PlusOutlined,
  FileTextOutlined,
  VideoCameraOutlined,
  PictureOutlined,
  AudioOutlined,
  ExperimentOutlined,
  ProjectOutlined,
  TeamOutlined,
  ReadOutlined,
  EditOutlined,
  DeleteOutlined,
  CrownOutlined,
  BranchesOutlined,
} from "@ant-design/icons";
import { supabase } from "../supabase";
import { useParams } from "react-router-dom";

const { Title } = Typography;

const LessonContent = () => {
  const { lessonId } = useParams();
  const [contents, setContents] = useState([]);
  const [lessonDetails, setLessonDetails] = useState({
    title: "",
    description: "",
  });
  const [loading, setLoading] = useState(true);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [form] = Form.useForm();
  const [parsedContent, setParsedContent] = useState(null);
  const [isPromptModalVisible, setIsPromptModalVisible] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [generatingContent, setGeneratingContent] = useState(false);
  const [isContentPromptModalVisible, setIsContentPromptModalVisible] =
    useState(false);
  const [selectedContentId, setSelectedContentId] = useState(null);

  useEffect(() => {
    fetchContents();
    fetchLessonDetails();
  }, []);

  const fetchContents = async () => {
    setGeneratingContent(true);
    try {
      const { data, error } = await supabase
        .from("lesson_contents")
        .select("*")
        .eq("lesson_id", lessonId);
      if (error) throw error;
      setContents(data.sort((a, b) => a.order - b.order));
    } catch (error) {
      message.error("Error fetching lesson contents");
    } finally {
      setLoading(false);
      setGeneratingContent(false);
    }
  };

  const fetchLessonDetails = async () => {
    try {
      const { data, error } = await supabase
        .from("lessons")
        .select("title, description")
        .eq("id", lessonId)
        .single();
      if (error) throw error;
      setLessonDetails(data);
    } catch (error) {
      message.error("Error fetching lesson details");
    }
  };

  const showPromptModal = () => {
    setIsPromptModalVisible(true);
  };

  const handlePromptCancel = () => {
    setIsPromptModalVisible(false);
    setPrompt("");
  };

  const handlePromptSubmit = async () => {
    setIsPromptModalVisible(false);
    setGeneratingContent(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/generate-lesson-content`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
            Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            lessonId,
            title: lessonDetails.title,
            description: lessonDetails.description,
            prompt,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to generate lesson content");
      }

      const { lessonContent } = await response.json();
      if (!Array.isArray(lessonContent)) {
        throw new Error("Generated content is not an array");
      }
      setContents((prevContents) => [...prevContents, ...lessonContent]);
      setGeneratingContent(false);
      message.success("Lesson content generated successfully");
    } catch (error) {
      message.error("Error generating lesson content");
    }
  };

  const deleteContent = async (contentId) => {
    try {
      const { error } = await supabase
        .from("lesson_contents")
        .delete()
        .eq("id", contentId);
      if (error) throw error;
      message.success("Content deleted successfully");
      fetchContents();
    } catch (error) {
      message.error("Error deleting content");
    }
  };

  const showModal = (content) => {
    setEditingContent(content);
    setParsedContent(JSON.parse(content.content_data));
    console.log(JSON.parse(content.content_data));
    setIsModalVisible(true);
    if (content) {
      form.setFieldsValue(content);
    } else {
      form.resetFields();
    }
  };

  const handleCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
  };

  const handleSubmit = async (values) => {
    try {
      values.content_data = JSON.stringify(JSON.parse(values.content_data));
    } catch (error) {
      message.error("Content data must be valid JSON");
      return;
    }
    values.lesson_id = lessonId;
    values.duration = parseInt(values.duration, 10);

    try {
      if (editingContent) {
        const { error } = await supabase
          .from("lesson_contents")
          .update(values)
          .eq("id", editingContent.id);
        if (error) throw error;
        message.success("Content updated successfully");
      } else {
        const { error } = await supabase
          .from("lesson_contents")
          .insert([values]);
        if (error) throw error;
        message.success("Content added successfully");
      }
      fetchContents();
      handleCancel();
    } catch (error) {
      message.error("Error saving content");
    }
  };

  const columns = [
    {
      title: "Title",
      dataIndex: "title",
      key: "title",
    },
    {
      title: "Content Type",
      dataIndex: "content_type",
      key: "content_type",
      render: (text) => {
        let icon;
        let title;
        switch (text) {
          case "interactive":
            icon = <ExperimentOutlined />;
            title = "Interactive Activity";
            break;
          case "exploration":
            icon = <ReadOutlined />;
            title = "Exploration Challenge";
            break;
          case "project":
            icon = <ProjectOutlined />;
            title = "Project-Based Task";
            break;
          case "simulation":
            icon = <VideoCameraOutlined />;
            title = "Simulation";
            break;
          case "discussion":
            icon = <TeamOutlined />;
            title = "Discussion or Debate";
            break;
          case "experiment":
            icon = <ExperimentOutlined />;
            title = "Hands-On Experiment";
            break;
          case "case_study":
            icon = <FileTextOutlined />;
            title = "Case Study";
            break;
          case "core_concepts":
            icon = <CrownOutlined />;
            title = "Core Concepts";
            break;
          case "syntax":
            icon = <BranchesOutlined />;
            title = "Syntax";
            break;
          default:
            icon = <FileTextOutlined />;
            title = "Unknown";
            break;
        }
        return (
          <span>
            {icon} {title}
          </span>
        );
      },
    },

    {
      title: "Duration",
      dataIndex: "duration",
      key: "duration",
    },

    {
      title: "Order",
      dataIndex: "order",
      key: "order",
    },
    {
      title: "Actions",
      key: "actions",
      render: (text, record) => (
        <div
          style={{
            display: "flex",
            gap: 8,
          }}
        >
          <>
            <Button onClick={() => showModal(record)}>
              <EditOutlined />
            </Button>
          </>
          <Popconfirm
            title="Are you sure you want to delete this lesson?"
            onConfirm={() => deleteContent(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button danger>
              <DeleteOutlined />
            </Button>
          </Popconfirm>
        </div>
      ),
    },
  ];

  return (
    <div style={{ padding: "24px" }}>
      <Title level={2}>{lessonDetails.title}</Title>
      <p>{lessonDetails.description}</p>
      <Modal
        title="Enter Prompt for Content Generation"
        visible={isPromptModalVisible}
        onCancel={handlePromptCancel}
        onOk={handlePromptSubmit}
      >
        <Form layout="vertical">
          <Form.Item
            label="Prompt"
            rules={[{ required: true, message: "Please input the prompt!" }]}
          >
            <Input.TextArea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Button
        type="default"
        onClick={showPromptModal}
        loading={generatingContent}
        style={{ marginBottom: 16, marginRight: 8 }}
      >
        Generate Content
      </Button>
      <Button
        type="primary"
        icon={<PlusOutlined />}
        onClick={() => showModal()}
        style={{ marginBottom: 16 }}
      >
        Add Content
      </Button>
      <Table
        columns={columns}
        dataSource={contents}
        loading={loading}
        rowKey="id"
      />
      <Modal
        title={editingContent ? "Edit Content" : "Add Content"}
        visible={isModalVisible}
        onCancel={handleCancel}
        onOk={form.submit}
        style={{ top: 20, minWidth: "80%" }}
        footer={[
          <Button
            key="ai-update"
            onClick={() => {
              setIsModalVisible(false);
              setSelectedContentId(editingContent.id);
              setIsContentPromptModalVisible(true);
            }}
            icon={<ExperimentOutlined />}
          >
            AI Update
          </Button>,
          <Button key="cancel" onClick={handleCancel}>
            Cancel
          </Button>,
          <Button key="submit" type="primary" onClick={form.submit}>
            Save
          </Button>,
        ]}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="title"
            label="Title"
            rules={[{ required: true, message: "Please input the title!" }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="content_type"
            label="Learning Module Type"
            rules={[
              {
                required: true,
                message: "Please select the learning module type!",
              },
            ]}
          >
            <Select>
              <Select.Option value="interactive">
                Interactive Activity
              </Select.Option>
              <Select.Option value="exploration">
                Exploration Challenge
              </Select.Option>
              <Select.Option value="project">Project-Based Task</Select.Option>
              <Select.Option value="simulation">Simulation</Select.Option>
              <Select.Option value="discussion">
                Discussion or Debate
              </Select.Option>
              <Select.Option value="experiment">
                Hands-On Experiment
              </Select.Option>
              <Select.Option value="case_study">Case Study</Select.Option>
            </Select>
          </Form.Item>
          <Form.Item
            name="order"
            label="Order"
            rules={[{ required: true, message: "Please input the order!" }]}
          >
            <Input type="number" />
          </Form.Item>
          <Form.Item
            name="duration"
            label="Duration (minutes)"
            rules={[{ required: true, message: "Please input the duration!" }]}
          >
            <Input type="number" />
          </Form.Item>

          {parsedContent && (
            <div
              style={{
                marginBottom: 24,
                border: "1px solid #d9d9d9",
                padding: 16,
                borderRadius: 4,
              }}
            >
              <Form.Item label="Hero Text" style={{ marginBottom: 16 }}>
                <Input.TextArea
                  value={parsedContent.heroText}
                  onChange={(e) => {
                    const updatedContent = {
                      ...parsedContent,
                      heroText: e.target.value,
                    };
                    setParsedContent(updatedContent);
                    form.setFieldsValue({
                      content_data: JSON.stringify(updatedContent, null, 2),
                    });
                  }}
                  style={{
                    fontWeight: "bold",
                  }}
                  rows={2}
                />
              </Form.Item>

              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={5}>Paragraphs</Typography.Title>
                {parsedContent.paragraphs &&
                  parsedContent.paragraphs.map((paragraph, index) => (
                    <div
                      key={index}
                      style={{
                        display: "flex",
                        gap: "8px",
                        marginBottom: "8px",
                      }}
                    >
                      <Input.TextArea
                        value={paragraph}
                        onChange={(e) => {
                          const updatedParagraphs = [
                            ...parsedContent.paragraphs,
                          ];
                          updatedParagraphs[index] = e.target.value;
                          const updatedContent = {
                            ...parsedContent,
                            paragraphs: updatedParagraphs,
                          };
                          setParsedContent(updatedContent);
                          form.setFieldsValue({
                            content_data: JSON.stringify(
                              updatedContent,
                              null,
                              2
                            ),
                          });
                        }}
                        rows={2}
                        style={{ flex: 1 }}
                      />
                      <Button
                        danger
                        onClick={() => {
                          const updatedParagraphs =
                            parsedContent.paragraphs.filter(
                              (_, i) => i !== index
                            );
                          const updatedContent = {
                            ...parsedContent,
                            paragraphs: updatedParagraphs,
                          };
                          setParsedContent(updatedContent);
                          form.setFieldsValue({
                            content_data: JSON.stringify(
                              updatedContent,
                              null,
                              2
                            ),
                          });
                        }}
                      >
                        <DeleteOutlined />
                      </Button>
                    </div>
                  ))}
                <Button
                  type="dashed"
                  onClick={() => {
                    const updatedParagraphs = [
                      ...(parsedContent.paragraphs || []),
                      "New paragraph",
                    ];
                    const updatedContent = {
                      ...parsedContent,
                      paragraphs: updatedParagraphs,
                    };
                    setParsedContent(updatedContent);
                    form.setFieldsValue({
                      content_data: JSON.stringify(updatedContent, null, 2),
                    });
                  }}
                  style={{ width: "100%" }}
                >
                  <PlusOutlined /> Add Paragraph
                </Button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={5}>Media</Typography.Title>
                
                <div style={{ marginBottom: 8 }}>
                  <Typography.Text strong>Image URL:</Typography.Text>
                  <Input
                    value={parsedContent.image}
                    onChange={(e) => {
                      const updatedContent = {
                        ...parsedContent,
                        image: e.target.value,
                      };
                      setParsedContent(updatedContent);
                      form.setFieldsValue({
                        content_data: JSON.stringify(updatedContent, null, 2),
                      });
                    }}
                    placeholder="Enter image URL"
                    style={{ marginTop: 4 }}
                  />
                  {parsedContent.image && (
                    <img
                      src={parsedContent.image}
                      alt="Content"
                      style={{
                        width: "100%",
                        height: "auto",
                        marginTop: 8,
                        marginBottom: 8,
                        maxWidth: 400,
                      }}
                    />
                  )}
                </div>

                <div style={{ marginBottom: 8 }}>
                  <Typography.Text strong>Video URL:</Typography.Text>
                  <Input
                    value={parsedContent.video}
                    onChange={(e) => {
                      const updatedContent = {
                        ...parsedContent,
                        video: e.target.value,
                      };
                      setParsedContent(updatedContent);
                      form.setFieldsValue({
                        content_data: JSON.stringify(updatedContent, null, 2),
                      });
                    }}
                    placeholder="Enter video URL (YouTube or direct link)"
                    style={{ marginTop: 4 }}
                  />
                  {parsedContent.video && (
                    <div style={{ marginTop: 8 }}>
                      {parsedContent.video.includes('youtube.com') || parsedContent.video.includes('youtu.be') ? (
                        <iframe
                          width="100%"
                          height="315"
                          src={parsedContent.video.replace('watch?v=', 'embed/')}
                          frameBorder="0"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          style={{ maxWidth: 560 }}
                        />
                      ) : (
                        <video
                          controls
                          style={{
                            width: '100%',
                            maxWidth: 560,
                            marginBottom: 8
                          }}
                        >
                          <source src={parsedContent.video} type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={5}>Activities</Typography.Title>
                {parsedContent.activities &&
                  parsedContent.activities.map((activity, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: 24,
                        border: "1px solid #d9d9d9",
                        padding: 16,
                        borderRadius: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "16px",
                        }}
                      >
                        <Input.TextArea
                          value={activity.title}
                          onChange={(e) => {
                            const updatedActivities = [
                              ...parsedContent.activities,
                            ];
                            updatedActivities[index] = {
                              ...activity,
                              title: e.target.value,
                            };
                            const updatedContent = {
                              ...parsedContent,
                              activities: updatedActivities,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(
                                updatedContent,
                                null,
                                2
                              ),
                            });
                          }}
                          placeholder="Activity title"
                          style={{ flex: 1 }}
                        />
                        <Button
                          danger
                          onClick={() => {
                            const updatedActivities =
                              parsedContent.activities.filter(
                                (_, i) => i !== index
                              );
                            const updatedContent = {
                              ...parsedContent,
                              activities: updatedActivities,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(
                                updatedContent,
                                null,
                                2
                              ),
                            });
                          }}
                        >
                          <DeleteOutlined />
                        </Button>
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong>Description:</Typography.Text>
                        <Input.TextArea
                          value={activity.description}
                          onChange={(e) => {
                            const updatedActivities = [
                              ...parsedContent.activities,
                            ];
                            updatedActivities[index] = {
                              ...activity,
                              description: e.target.value,
                            };
                            const updatedContent = {
                              ...parsedContent,
                              activities: updatedActivities,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(
                                updatedContent,
                                null,
                                2
                              ),
                            });
                          }}
                          placeholder="Activity description"
                          style={{ marginTop: 8 }}
                        />
                      </div>

                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong>Images:</Typography.Text>
                        {activity.images && activity.images.map((img, imgIndex) => (
                          <div key={imgIndex} style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 8 }}>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <Input
                                value={img.url}
                                onChange={(e) => {
                                  const updatedActivities = [...parsedContent.activities];
                                  const updatedImages = [...activity.images];
                                  updatedImages[imgIndex] = {
                                    ...updatedImages[imgIndex],
                                    url: e.target.value
                                  };
                                updatedActivities[index] = {
                                  ...activity,
                                  images: updatedImages,
                                };
                                const updatedContent = {
                                  ...parsedContent,
                                  activities: updatedActivities,
                                };
                                setParsedContent(updatedContent);
                                form.setFieldsValue({
                                  content_data: JSON.stringify(updatedContent, null, 2),
                                });
                              }}
                                placeholder="Image URL"
                                style={{ flex: 1 }}
                              />
                              <Input
                                value={img.alt}
                                onChange={(e) => {
                                  const updatedActivities = [...parsedContent.activities];
                                  const updatedImages = [...activity.images];
                                  updatedImages[imgIndex] = {
                                    ...updatedImages[imgIndex],
                                    alt: e.target.value
                                  };
                                  updatedActivities[index] = {
                                    ...activity,
                                    images: updatedImages,
                                  };
                                  const updatedContent = {
                                    ...parsedContent,
                                    activities: updatedActivities,
                                  };
                                  setParsedContent(updatedContent);
                                  form.setFieldsValue({
                                    content_data: JSON.stringify(updatedContent, null, 2),
                                  });
                                }}
                                placeholder="Alt Text"
                                style={{ flex: 1 }}
                              />
                            <Button
                              danger
                              onClick={() => {
                                const updatedActivities = [...parsedContent.activities];
                                const updatedImages = activity.images.filter((_, i) => i !== imgIndex);
                                updatedActivities[index] = {
                                  ...activity,
                                  images: updatedImages,
                                };
                                const updatedContent = {
                                  ...parsedContent,
                                  activities: updatedActivities,
                                };
                                setParsedContent(updatedContent);
                                form.setFieldsValue({
                                  content_data: JSON.stringify(updatedContent, null, 2),
                                });
                              }}
                            >
                              <DeleteOutlined />
                            </Button>
                          </div>
                        </div>
                        ))}
                        {activity.images && activity.images.length > 0 && (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {activity.images.map((img, imgIndex) => (
                              <img
                                key={imgIndex}
                                src={img.url}
                                alt={img.alt || `Activity ${index + 1} image ${imgIndex + 1}`}
                                style={{
                                  width: '200px',
                                  height: '150px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                }}
                              />
                            ))}
                          </div>
                        )}
                        <Button
                          type="dashed"
                          onClick={() => {
                            const updatedActivities = [...parsedContent.activities];
                            updatedActivities[index] = {
                              ...activity,
                              images: [...(activity.images || []), { url: '', alt: '' }],
                            };
                            const updatedContent = {
                              ...parsedContent,
                              activities: updatedActivities,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(updatedContent, null, 2),
                            });
                          }}
                          style={{ marginTop: 8, width: '100%' }}
                        >
                          <PlusOutlined /> Add Image
                        </Button>
                      </div>

                      <div style={{ marginTop: 16 }}>
                        <Typography.Text strong>Checklist:</Typography.Text>
                        {activity &&
                        activity.checklist &&
                        activity.checklist.length > 0 &&
                        activity.checklist.map((item, itemIndex) => (
                          <div
                            key={itemIndex}
                            style={{
                              display: "flex",
                              gap: "8px",
                              marginTop: "8px",
                            }}
                          >
                            <Input
                              value={item.text}
                              onChange={(e) => {
                                const updatedActivities = [
                                  ...parsedContent.activities,
                                ];
                                const updatedChecklist = [
                                  ...activity.checklist,
                                ];
                                updatedChecklist[itemIndex] = {
                                  ...item,
                                  text: e.target.value,
                                };
                                updatedActivities[index] = {
                                  ...activity,
                                  checklist: updatedChecklist,
                                };
                                const updatedContent = {
                                  ...parsedContent,
                                  activities: updatedActivities,
                                };
                                setParsedContent(updatedContent);
                                form.setFieldsValue({
                                  content_data: JSON.stringify(
                                    updatedContent,
                                    null,
                                    2
                                  ),
                                });
                              }}
                              style={{ flex: 1 }}
                            />
                            <Button
                              danger
                              onClick={() => {
                                const updatedActivities = [
                                  ...parsedContent.activities,
                                ];
                                const updatedChecklist =
                                  activity.checklist.filter(
                                    (_, i) => i !== itemIndex
                                  );
                                updatedActivities[index] = {
                                  ...activity,
                                  checklist: updatedChecklist,
                                };
                                const updatedContent = {
                                  ...parsedContent,
                                  activities: updatedActivities,
                                };
                                setParsedContent(updatedContent);
                                form.setFieldsValue({
                                  content_data: JSON.stringify(
                                    updatedContent,
                                    null,
                                    2
                                  ),
                                });
                              }}
                            >
                              <DeleteOutlined />
                            </Button>
                          </div>
                        ))}
                      </div>

                      <Button
                        type="dashed"
                        onClick={() => {
                          const updatedActivities = [
                            ...parsedContent.activities,
                          ];
                          updatedActivities[index] = {
                            ...activity,
                            checklist: [
                              ...(activity.checklist || []),
                              {
                                id: (activity.checklist.length + 1).toString(),
                                text: "New checklist item",
                              },
                            ],
                          };
                          const updatedContent = {
                            ...parsedContent,
                            activities: updatedActivities,
                          };
                          setParsedContent(updatedContent);
                          form.setFieldsValue({
                            content_data: JSON.stringify(
                              updatedContent,
                              null,
                              2
                            ),
                          });
                        }}
                        style={{ marginTop: 8, width: "100%" }}
                      >
                        <PlusOutlined /> Add Checklist Item
                      </Button>
                    </div>
                  ))}

                <Button
                  type="dashed"
                  onClick={() => {
                    const newActivity = {
                      title: "New Activity",
                      description: "",
                      images: [{
                        url: "",
                        alt: ""
                      }],
                      video: "",
                      checklist: [
                        {
                          id: "1",
                          text: "First step",
                        },
                      ],
                    };
                    const updatedActivities = [
                      ...(parsedContent.activities || []),
                      newActivity,
                    ];
                    const updatedContent = {
                      ...parsedContent,
                      activities: updatedActivities,
                    };
                    setParsedContent(updatedContent);
                    form.setFieldsValue({
                      content_data: JSON.stringify(updatedContent, null, 2),
                    });
                  }}
                  style={{ width: "100%" }}
                >
                  <PlusOutlined /> Add Activity
                </Button>
              </div>

              <div style={{ marginBottom: 16 }}>
                <Typography.Title level={5}>Questions</Typography.Title>
                {parsedContent.questions &&
                  parsedContent.questions.map((question, index) => (
                    <div
                      key={index}
                      style={{
                        marginBottom: 24,
                        border: "1px solid #d9d9d9",
                        padding: 16,
                        borderRadius: 4,
                      }}
                    >
                      <div
                        style={{
                          display: "flex",
                          gap: "8px",
                          marginBottom: "16px",
                        }}
                      >
                        <Input.TextArea
                          value={question.question}
                          onChange={(e) => {
                            const updatedQuestions = [
                              ...parsedContent.questions,
                            ];
                            updatedQuestions[index] = {
                              ...question,
                              question: e.target.value,
                            };
                            const updatedContent = {
                              ...parsedContent,
                              questions: updatedQuestions,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(
                                updatedContent,
                                null,
                                2
                              ),
                            });
                          }}
                          placeholder="Question text"
                          style={{ flex: 1 }}
                        />
                        <Button
                          danger
                          onClick={() => {
                            const updatedQuestions =
                              parsedContent.questions.filter(
                                (_, i) => i !== index
                              );
                            const updatedContent = {
                              ...parsedContent,
                              questions: updatedQuestions,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(
                                updatedContent,
                                null,
                                2
                              ),
                            });
                          }}
                        >
                          <DeleteOutlined />
                        </Button>
                      </div>

                      <Typography.Text strong>
                        Options: (Select correct answer)
                      </Typography.Text>
                      {question.options.map((option, optIndex) => (
                        <div
                          key={optIndex}
                          style={{
                            display: "flex",
                            gap: "8px",
                            marginTop: "8px",
                            backgroundColor:
                              option === question.answer.replace(".", "") ||
                              option === question.answer
                                ? "#f6ffed"
                                : "transparent",
                            padding: "4px",
                            borderRadius: "4px",
                            border:
                              option === question.answer.replace(".", "") ||
                              option === question.answer
                                ? "1px solid #b7eb8f"
                                : "none",
                          }}
                        >
                          <Radio
                            checked={
                              option === question.answer.replace(".", "") ||
                              option === question.answer
                            }
                            onChange={() => {
                              const updatedQuestions = [
                                ...parsedContent.questions,
                              ];
                              updatedQuestions[index] = {
                                ...question,
                                answer: option,
                              };
                              const updatedContent = {
                                ...parsedContent,
                                questions: updatedQuestions,
                              };
                              setParsedContent(updatedContent);
                              form.setFieldsValue({
                                content_data: JSON.stringify(
                                  updatedContent,
                                  null,
                                  2
                                ),
                              });
                            }}
                          />
                          <Input
                            value={option}
                            onChange={(e) => {
                              const updatedQuestions = [
                                ...parsedContent.questions,
                              ];
                              const updatedOptions = [...question.options];
                              updatedOptions[optIndex] = e.target.value;
                              updatedQuestions[index] = {
                                ...question,
                                options: updatedOptions,
                                answer:
                                  question.answer == option
                                    ? e.target.value
                                    : question.answer,
                              };
                              const updatedContent = {
                                ...parsedContent,
                                questions: updatedQuestions,
                              };
                              setParsedContent(updatedContent);
                              form.setFieldsValue({
                                content_data: JSON.stringify(
                                  updatedContent,
                                  null,
                                  2
                                ),
                              });
                            }}
                            style={{ flex: 1 }}
                          />
                          <Button
                            danger
                            onClick={() => {
                              const updatedQuestions = [
                                ...parsedContent.questions,
                              ];
                              const updatedOptions = question.options.filter(
                                (_, i) => i !== optIndex
                              );
                              updatedQuestions[index] = {
                                ...question,
                                options: updatedOptions,
                                answer:
                                  question.answer == option
                                    ? ""
                                    : question.answer,
                              };
                              const updatedContent = {
                                ...parsedContent,
                                questions: updatedQuestions,
                              };
                              setParsedContent(updatedContent);
                              form.setFieldsValue({
                                content_data: JSON.stringify(
                                  updatedContent,
                                  null,
                                  2
                                ),
                              });
                            }}
                          >
                            <DeleteOutlined />
                          </Button>
                        </div>
                      ))}

                      <div
                        style={{
                          marginTop: 8,
                        }}
                      >
                        <Typography.Text strong>
                          Correct Answer:
                        </Typography.Text>
                        {question.answer}
                      </div>

                      <Button
                        type="dashed"
                        onClick={() => {
                          const updatedQuestions = [...parsedContent.questions];
                          updatedQuestions[index] = {
                            ...question,
                            options: [...question.options, "New option"],
                          };
                          const updatedContent = {
                            ...parsedContent,
                            questions: updatedQuestions,
                          };
                          setParsedContent(updatedContent);
                          form.setFieldsValue({
                            content_data: JSON.stringify(
                              updatedContent,
                              null,
                              2
                            ),
                          });
                        }}
                        style={{ marginTop: 8, width: "100%" }}
                      >
                        <PlusOutlined /> Add Option
                      </Button>

                      <div style={{ marginBottom: 16 }}>
                        <Typography.Text strong>Images:</Typography.Text>
                        {question.images && question.images.map((img, imgIndex) => (
                          <div key={imgIndex} style={{ marginTop: 8, display: 'flex', gap: 8, alignItems: 'center' }}>
                            <Input
                              value={img}
                              onChange={(e) => {
                                const updatedQuestions = [...parsedContent.questions];
                                const updatedImages = [...question.images];
                                updatedImages[imgIndex] = e.target.value;
                                updatedQuestions[index] = {
                                  ...question,
                                  images: updatedImages,
                                };
                                const updatedContent = {
                                  ...parsedContent,
                                  questions: updatedQuestions,
                                };
                                setParsedContent(updatedContent);
                                form.setFieldsValue({
                                  content_data: JSON.stringify(updatedContent, null, 2),
                                });
                              }}
                              placeholder="Image URL"
                              style={{ flex: 1 }}
                            />
                            <Button
                              danger
                              onClick={() => {
                                const updatedQuestions = [...parsedContent.questions];
                                const updatedImages = question.images.filter((_, i) => i !== imgIndex);
                                updatedQuestions[index] = {
                                  ...question,
                                  images: updatedImages,
                                };
                                const updatedContent = {
                                  ...parsedContent,
                                  questions: updatedQuestions,
                                };
                                setParsedContent(updatedContent);
                                form.setFieldsValue({
                                  content_data: JSON.stringify(updatedContent, null, 2),
                                });
                              }}
                            >
                              <DeleteOutlined />
                            </Button>
                          </div>
                        ))}
                        {question.images && question.images.length > 0 && (
                          <div style={{ marginTop: 8, display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {question.images.map((img, imgIndex) => (
                              <img
                                key={imgIndex}
                                src={img}
                                alt={`Question ${index + 1} image ${imgIndex + 1}`}
                                style={{
                                  width: '200px',
                                  height: '150px',
                                  objectFit: 'cover',
                                  borderRadius: '4px',
                                }}
                              />
                            ))}
                          </div>
                        )}
                        <Button
                          type="dashed"
                          onClick={() => {
                            const updatedQuestions = [...parsedContent.questions];
                            updatedQuestions[index] = {
                              ...question,
                              images: [...(question.images || []), ''],
                            };
                            const updatedContent = {
                              ...parsedContent,
                              questions: updatedQuestions,
                            };
                            setParsedContent(updatedContent);
                            form.setFieldsValue({
                              content_data: JSON.stringify(updatedContent, null, 2),
                            });
                          }}
                          style={{ marginTop: 8, width: '100%' }}
                        >
                          <PlusOutlined /> Add Image
                        </Button>
                      </div>

                      <Input.TextArea
                        value={question.explanation}
                        onChange={(e) => {
                          const updatedQuestions = [...parsedContent.questions];
                          updatedQuestions[index] = {
                            ...question,
                            explanation: e.target.value,
                          };
                          const updatedContent = {
                            ...parsedContent,
                            questions: updatedQuestions,
                          };
                          setParsedContent(updatedContent);
                          form.setFieldsValue({
                            content_data: JSON.stringify(
                              updatedContent,
                              null,
                              2
                            ),
                          });
                        }}
                        placeholder="Explanation"
                        style={{ marginTop: 16 }}
                      />
                    </div>
                  ))}

                <Button
                  type="dashed"
                  onClick={() => {
                    const newQuestion = {
                      question: "New question",
                      options: ["Option 1", "Option 2"],
                      answer: "",
                      explanation: "",
                    };
                    const updatedQuestions = [
                      ...(parsedContent.questions || []),
                      newQuestion,
                    ];
                    const updatedContent = {
                      ...parsedContent,
                      questions: updatedQuestions,
                    };
                    setParsedContent(updatedContent);
                    form.setFieldsValue({
                      content_data: JSON.stringify(updatedContent, null, 2),
                    });
                  }}
                  style={{ width: "100%" }}
                >
                  <PlusOutlined /> Add Question
                </Button>
              </div>
            </div>
          )}
          <Form.Item
            style={{ display: "none" }}
            name="content_data"
            label="Content Data"
            rules={[
              { required: true, message: "Please input the content data!" },
            ]}
          >
            <Input.TextArea
              rows={12}
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  setParsedContent(parsed);
                } catch (error) {
                  setParsedContent(null);
                }
              }}
            />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title="Update Content with AI"
        visible={isContentPromptModalVisible}
        onCancel={() => {
          setIsContentPromptModalVisible(false);
          setPrompt("");
          setSelectedContentId(null);
        }}
        onOk={async () => {
          try {
            setGeneratingContent(true);
            const response = await fetch(
              `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/update-content-data`,
              {
                method: "POST",
                headers: {
                  "Content-Type": "application/json",
                  apikey: process.env.REACT_APP_SUPABASE_ANON_KEY,
                  Authorization: `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
                },
                body: JSON.stringify({
                  contentId: selectedContentId,
                  prompt,
                }),
              }
            );

            if (!response.ok) {
              throw new Error("Failed to update content");
            }

            const { updatedContent } = await response.json();
            setContents(
              contents.map((content) =>
                content.id === selectedContentId ? updatedContent : content
              )
            );

            message.success("Content updated successfully");
            setIsContentPromptModalVisible(false);
            setPrompt("");
            setSelectedContentId(null);
          } catch (error) {
            message.error("Error updating content: " + error.message);
          } finally {
            setGeneratingContent(false);
          }
        }}
        confirmLoading={generatingContent}
      >
        <Form layout="vertical">
          <Form.Item
            label="How would you like to modify this content?"
            rules={[{ required: true, message: "Please input your prompt!" }]}
          >
            <Input.TextArea
              value={prompt}
              onChange={(e) => setPrompt(e.target.value)}
              rows={4}
              placeholder="E.g., Add more examples, Make it more engaging, Add a new activity..."
            />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default LessonContent;
